import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import axios from "axios";
import { toast } from "react-toastify";
import { useLanguage } from "LanguageContext";
import LanguageSwitcher from "LanguageSwitcher";
import * as URLS from "../../urls";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const translations = {
    en: {
      title: "FitAdmin Pro",
      subtitle: "Fitness Center Management System",
      welcome: "Welcome Back",
      loginDesc: "Sign in to your admin account",
      email: "Email Address",
      password: "Password",
      showPassword: "Show Password",
      hidePassword: "Hide Password",
      signIn: "Sign In",
      forgotPassword: "Forgot Password?",
      invalidCredentials:
        "Invalid credentials. Use admin@fitpro.com / FitAdmin123",
      poweredBy: "Powered by FitAdmin Pro",
      allRights: "All rights reserved.",
    },
    ar: {
      title: "فيت أدمن برو",
      subtitle: "نظام إدارة مراكز اللياقة البدنية",
      welcome: "مرحباً بعودتك",
      loginDesc: "تسجيل الدخول إلى حساب المدير",
      email: "عنوان البريد الإلكتروني",
      password: "كلمة المرور",
      showPassword: "إظهار كلمة المرور",
      hidePassword: "إخفاء كلمة المرور",
      signIn: "تسجيل الدخول",
      forgotPassword: "نسيت كلمة المرور؟",
      invalidCredentials:
        "بيانات اعتماد غير صحيحة. استخدم admin@fitpro.com / FitAdmin123",
      poweredBy: "مدعوم من فيت أدمن برو",
      allRights: "جميع الحقوق محفوظة.",
    },
  };

  const { language } = useLanguage();
  const t = translations[language];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.email !== "" && formData.password !== "") {
      setLoading(true); // Start loading

      axios
        .post(`${URLS.baseUrl}/${URLS?.loginURL}`, formData)
        .then((response) => {
          if (response?.status === 200) {
            localStorage.setItem("Token", response?.data?.data?.token);
            localStorage.setItem(
              "permissions",
              JSON.stringify(response?.data?.data?.user?.permissions)
            );
            localStorage.setItem(
              "user",
              JSON.stringify({
                id: response.data.data?.user?.id,
                name: response.data.data?.user?.name,
                email: response.data.data?.user?.email,
              })
            );
            navigate("/dashboard-overview");
          } else {
            response?.payload?.errors?.map((error) => toast.warn(error));
          }
        })
        .catch((error) => {
          toast.error("An error occurred");
        })
        .finally(() => {
          setLoading(false); // Stop loading
        });
    } else {
      setError(t.invalidCredentials);
    }
  };

  useEffect(() => {
    // localStorage.clear();
  }, []);

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4 ${
        language == "ar" ? "rtl" : "ltr"
      }`}
    >
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left Side - Branding */}
        <div className="hidden lg:flex flex-col items-center justify-center text-center p-8">
          <div className="mb-8">
            <div className="w-24 h-24 bg-green-600 rounded-full flex items-center justify-center mb-6 mx-auto">
              <Icon name="Dumbbell" size={48} color="white" />
            </div>
            <h1 className="text-4xl font-bold text-green-800 mb-2">
              {t.title}
            </h1>
            <p className="text-xl text-green-600 mb-8">{t.subtitle}</p>
          </div>

          <div className="w-full max-w-md">
            <Image
              src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
              alt="Fitness Center"
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Language Toggle */}
            <div className="flex justify-end mb-6">
              <LanguageSwitcher />
            </div>

            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Icon name="Dumbbell" size={32} color="white" />
              </div>
              <h1 className="text-2xl font-bold text-green-800">{t.title}</h1>
            </div>

            {/* Form Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t.welcome}
              </h2>
              <p className="text-gray-600">{t.loginDesc}</p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className={`block text-sm font-medium text-gray-700 mb-2 ${
                    language == "ar" ? "text-right" : "text-left"
                  }`}
                >
                  {t.email}
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                    placeholder="admin@fitpro.com"
                    required
                  />
                  <Icon
                    name="Mail"
                    size={20}
                    className={`absolute top-3.5 text-gray-400 ${
                      language == "ar" ? "left-4" : "right-4"
                    }`}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                 
                     className={`block text-sm font-medium text-gray-700 mb-2 ${
                    language == "ar" ? "text-right" : "text-left"
                  }`}
                >
                  {t.password}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                    placeholder="FitAdmin123"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute top-3.5 text-gray-400 hover:text-gray-600 ${
                      language == "ar" ? "left-4" : "right-4"
                    }`}
                    title={showPassword ? t.hidePassword : t.showPassword}
                  >
                    <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors font-medium"
              >
                {loading ? "Loading..." : t.signIn}
              </button>

              <div className="text-center">
                {/* Uncomment if you want to add a forgot password link */}
                {/* <button type="button" className="text-green-600 hover:text-green-700 text-sm font-medium">{t.forgotPassword}</button> */}
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 text-sm text-gray-600">
            <p>{t.poweredBy}</p>
            <p>
              &copy; {new Date().getFullYear()} {t.allRights}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
